import { enableProdMode, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { RouterModule, Routes } from '@angular/router';
import { CommonModule, DatePipe } from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';
import {
  GridModule, FilterService, PageService, SortService, ForeignKeyService, EditService, CommandColumnService,
  SearchService, ToolbarService
} from '@syncfusion/ej2-angular-grids';
import { NgbModule, NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {ContenteditableValueAccessorModule} from '@tinkoff/tui-editor/angular-contenteditable-accessor';

import {
  StoreRouterConnectingModule,
  RouterStateSerializer
} from '@ngrx/router-store';
import { ActionReducer, StoreModule, MetaReducer } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';

import * as fromStateManagement from './state-management';

// // not used in production
import { StoreDevtoolsModule } from '@ngrx/store-devtools';

import { NgxGalleryModule } from '@vinlos/ngx-gallery';

import { environment } from '../environments/environment';
import { AppComponent } from './app.component';

import * as fromComponents from './components';

// containers
import * as fromContainers from './containers';

// services
import * as fromServices from './services';

// guards
import * as fromGuards from './guards';

import { QuillModule } from 'ngx-quill';

import {
  DnvUiModule, DnvLibModule, dnvMapReducer, layerReducer,
  LayerEffects, BaseMapEffects, baseMapReducer,
  DnvSearchEffects, dnvSearchReducer,
  ToastrEffects, legendReducer, DnvLegendEffects
} from 'dnv-lib';

import { storeLogger } from 'ngrx-store-logger';
import { DashboardGuard, WorkRequestGuard, AdminGuard } from './guards';
import { MyTasksGuard } from './guards/mytasks/mytasks.guard';
import { RequestInterceptor } from './interceptor/http.interceptor';

import { IPublicClientApplication, PublicClientApplication, BrowserCacheLocation, LogLevel, InteractionType } from '@azure/msal-browser';
import {
  MsalBroadcastService, MsalGuard, MsalGuardConfiguration, MsalInterceptor, MsalInterceptorConfiguration
  , MsalModule, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG
} from '@azure/msal-angular';
import { NgxMaskDirective, provideEnvironmentNgxMask, NgxMaskPipe } from 'ngx-mask';
import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { AdminComponent } from './containers/admin/admin.component';
import { NgSelectModule } from '@ng-select/ng-select';

// import { httpInterceptorProviders, ArcGisTokenGuard } from './arcgis-interceptors';

// Declare that the window object can have an environment property.
// This prevents some implicit any types.
declare global {
  interface Window {
    environment: string | null;
  }
}

const routes: Routes = [
  // { path: 'loading', component: fromContainers.LoadingComponent, canActivate: [MsalGuard] },
  { path: 'unauthorized', component: fromComponents.AccessErrorComponent },
  { path: 'dashboard', component: fromContainers.DashboardComponent, canActivate: [MsalGuard] },
  { path: 'mytasks', component: fromContainers.MyTasksComponent, canActivate: [MsalGuard] },
  { path: 'problemdetails', component: fromContainers.ProblemDetailComponent, canActivate: [MsalGuard] },
  { path: 'problemlocation', component: fromContainers.ProblemLocationComponent, canActivate: [MsalGuard] },
  { path: 'problemhistory', component: fromContainers.ProblemHistoryComponent, canActivate: [MsalGuard] },
  { path: 'addcase', component: fromContainers.AddCaseComponent, canActivate: [MsalGuard] },
  { path: 'activities', component: fromComponents.ActivityComponent, canActivate: [MsalGuard] },
  { path: 'logout', component: fromComponents.LogoutComponent },
  { path: 'search', component: fromContainers.SearchComponent, canActivate: [MsalGuard] },
  { path: 'workrequest', component: fromContainers.WorkRequestComponent, canActivate: [MsalGuard] },
  { path: 'admin', component: AdminComponent, canActivate: [MsalGuard, AdminGuard] },
  { path: 'recentproblems', component: fromContainers.RecentProblemsComponent, canActivate: [MsalGuard] },
  { path: '**', redirectTo: '/dashboard' }
];

export function loggerCallback(logLevel: LogLevel, message: string, piiEnabled: boolean) {
  // console.log('client logging' + message);
}

const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;

export function log(reducer: ActionReducer<any>): ActionReducer<any> {
  const verbose = true;

  if (window['environment'] === 'Development') {
    return storeLogger()(reducer);
  } else {
    if (window['appConfig']) {
      return storeLogger(window['appConfig']['loggingOption'])(reducer);
    }

    return storeLogger()(reducer);
  }
}

export function appEnvironment(value: string): boolean {
  return value === (window['environment'] || '');
}
if (appEnvironment('production')) {
  enableProdMode();
}

const metaReducers: MetaReducer<any>[] = [log];
const reducers = {
  ...fromStateManagement.reducers,
  mapState: dnvMapReducer
};

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.auth.clientId,
      authority: environment.auth.authority,
      redirectUri: environment.auth.redirectUri,
      postLogoutRedirectUri: environment.auth.logoutUri,
      navigateToLoginRequestUrl: true,  // allow redirect to the link you came from if you were asked to login.
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: false
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);
  protectedResourceMap.set(environment.webApiUrl, ['openid']);
  protectedResourceMap.set('api', ['openid']);
  protectedResourceMap.set('arcgis', ['openid']);
  protectedResourceMap.set(environment.webApiUrl + '/arcgis/*', ['openid']);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read']
    }
  };
}

@NgModule({ declarations: [
        AppComponent,
        ...fromContainers.containers,
        ...fromComponents.components
    ],
    bootstrap: [AppComponent], 
    imports: [
        NgSelectModule,
        BrowserModule,
        CommonModule, // 1/3 ngx-toastr requirements:
        BrowserAnimationsModule, // 2/3 required animations module
        ToastrModule.forRoot({
            // disableTimeOut: true, // Set to true for errors so that they stay visible
            positionClass: 'toast-bottom-right',
            preventDuplicates: false,
        }),
        GridModule,
        FormsModule,
        ReactiveFormsModule,
        RouterModule.forRoot(routes, {
            useHash: false,
            anchorScrolling: 'enabled',
            onSameUrlNavigation: 'reload',
            enableTracing: true,
            scrollPositionRestoration: 'enabled'
        }),
        StoreModule.forRoot(reducers, {
            metaReducers,
            runtimeChecks: {
                strictStateImmutability: false,
                strictActionImmutability: false
            },
        }),
        StoreRouterConnectingModule.forRoot(),
        !environment.production ? [] : StoreDevtoolsModule.instrument(),
        EffectsModule.forRoot([
            ...fromStateManagement.effects
        ]),
        DnvUiModule,
        DnvLibModule,
        NgxGalleryModule,
        NgbModule,
        ContenteditableValueAccessorModule,
        QuillModule.forRoot({
            modules: {
                toolbar: false
            },
            placeholder: ''
        }),
        MsalModule,
        NgxSliderModule,
        NgxMaskPipe,
        NgxMaskDirective], providers: [
        { provide: RouterStateSerializer, useClass: fromStateManagement.CustomSerializer },
        [
            { provide: HTTP_INTERCEPTORS, useClass: RequestInterceptor, multi: true }
        ],
        [provideEnvironmentNgxMask()],
        // ...httpInterceptorProviders,
        // ArcGisTokenGuard,
        ...fromServices.services,
        ...fromGuards.guards,
        FilterService,
        PageService,
        SortService,
        ForeignKeyService,
        DatePipe,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        NgbActiveModal,
        EditService,
        CommandColumnService,
        ToolbarService,
        SearchService,
        ForeignKeyService,
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule { }
// declare module '@angular/core' {
//   interface ModuleWithProviders<T = any> {
//     ngModule: Type<T>;
//     providers?: Provider[];
//   }
// }
